import React, { useEffect, useRef, useState, useCallback } from "react";
import { getAudioConfig } from "../../api/audio/audioConfigApi";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Box,
  Stack,
  Divider,
  Link,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const StressReliefDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [expandAboutMusic, setExpandAboutMusic] = useState(false);
  const [audioError, setAudioError] = useState("");

  const FALLBACK_AUDIO_URL = "https://storage.googleapis.com/mindsherpa-app-bucket/audios/Wavelengths-Alpha.mp3";
  const [audioUrl, setAudioUrl] = useState(FALLBACK_AUDIO_URL);
  const [audioLoop, setAudioLoop] = useState(false);
  const hasRetriedWithFallbackRef = useRef(false);

  const loadAudioConfig = useCallback(async () => {
    try {
      const config = await getAudioConfig();
      const hasBackendAudioUrl = Boolean(config?.enabled && config?.audioUrl);

      setAudioUrl(hasBackendAudioUrl ? config.audioUrl : FALLBACK_AUDIO_URL);
      setAudioLoop(hasBackendAudioUrl && config?.loop !== undefined ? Boolean(config.loop) : false);
      setAudioError("");
      hasRetriedWithFallbackRef.current = false;

      if (audioRef.current) {
        if (config?.volume !== undefined) {
          audioRef.current.volume = Math.min(1, Math.max(0, config.volume));
        }
        audioRef.current.currentTime = 0;
      }
    } catch {
      setAudioUrl(FALLBACK_AUDIO_URL);
      setAudioLoop(false);
      setAudioError("");
      hasRetriedWithFallbackRef.current = false;

      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadAudioConfig();
    }
  }, [open, loadAudioConfig]);

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        const playPromise = audioRef.current.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {
            setAudioError("Audio source is unavailable in this environment.");
            setIsPlaying(false);
          });
        }
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleProgressChange = (e) => {
    const newTime = (e.target.value / 100) * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      setCurrentTime(0);
    }
    hasRetriedWithFallbackRef.current = false;
    setAudioError("");
    onClose();
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const progressPercent = duration ? (currentTime / duration) * 100 : 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-container": {
          justifyContent: "flex-end",
          alignItems: "stretch",
        },
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.3)",
        },
      }}
      PaperProps={{
        sx: {
          position: "fixed",
          right: 0,
          top: 0,
          bottom: 0,
          margin: 0,
          maxHeight: "100vh",
          height: "100vh",
          borderRadius: 0,
          boxShadow: "-4px 0 16px rgba(0, 0, 0, 0.1)",
          maxWidth: { xs: "100vw", sm: "400px", md: "500px" },
          width: { xs: "100vw", sm: "400px", md: "500px" },
        },
      }}
      TransitionProps={{
        timeout: 300,
      }}
      transitionDuration={300}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="h6" fontWeight="600">
            Stress Relief & Focus (Alpha Waves)
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            Ideal for calming the mind before a Morning Focus
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Duration & Courtesy Row */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  letterSpacing: 0.5,
                }}
              >
                DURATION
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.primary, mt: 0.5 }}
              >
                30 minutes
              </Typography>
            </Box>
            <Link
              href="https://indiemusicbox.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: "rgba(10, 114, 83, 0.08)",
                border: "1px solid rgba(10, 114, 83, 0.22)",
                borderRadius: "8px",
                px: 1.2,
                py: 0.5,
                color: theme.palette.primary.main,
                fontSize: "0.75rem",
                fontWeight: 600,
                textDecoration: "none",
                "&:hover": {
                  backgroundColor: theme.palette.primary.main,
                  color: "#FFFFFF",
                },
                transition: "all 0.2s ease",
              }}
            >
              Courtesy of Chris Collins
              <OpenInNewIcon sx={{ fontSize: 13 }} />
            </Link>
          </Box>

          {/* Description */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.6,
              }}
            >
              Immerse yourself in relaxing music to quickly dissolve stress and
              sharpen your focus before you begin your reflection. Best for
              clearing morning fog or calming mid-day anxiety.
            </Typography>
          </Box>

          {/* Audio Player */}
          <Box
            sx={{
              backgroundColor: theme.palette.mode === "dark" ? "#f5f5f5" : "#f9f9f9",
              borderRadius: 2,
              p: 2,
            }}
          >
            <audio
              ref={audioRef}
              src={audioUrl}
              loop={audioLoop}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onError={() => {
                if (audioUrl !== FALLBACK_AUDIO_URL && !hasRetriedWithFallbackRef.current) {
                  hasRetriedWithFallbackRef.current = true;
                  setAudioUrl(FALLBACK_AUDIO_URL);
                  setAudioLoop(false);
                  setCurrentTime(0);
                  setDuration(0);
                  return;
                }

                setAudioError("Audio source is unavailable in this environment.");
                setIsPlaying(false);
              }}
              onEnded={() => { if (!audioLoop) setIsPlaying(false); }}
            />

            {/* Player Controls */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Button
                onClick={handlePlayPause}
                variant="contained"
                sx={{
                  minWidth: 56,
                  minHeight: 56,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }}
              >
                {isPlaying ? (
                  <PauseIcon sx={{ color: "white" }} />
                ) : (
                  <PlayArrowIcon sx={{ color: "white" }} />
                )}
              </Button>

              <VolumeUpIcon
                sx={{ color: theme.palette.text.secondary, fontSize: 20 }}
              />
            </Stack>

            {audioError && (
              <Typography
                variant="caption"
                sx={{ color: theme.palette.error.main, display: "block", mb: 1 }}
              >
                {audioError}
              </Typography>
            )}

            {/* Progress Bar */}
            <Box sx={{ mb: 1 }}>
              <input
                type="range"
                min="0"
                max="100"
                value={progressPercent}
                onChange={handleProgressChange}
                style={{
                  width: "100%",
                  height: 4,
                  borderRadius: 2,
                  outline: "none",
                  background: theme.palette.mode === "dark" 
                    ? "rgba(255,255,255,0.1)" 
                    : "rgba(0,0,0,0.1)",
                  WebkitAppearance: "none",
                }}
              />
            </Box>

            {/* Time Display */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {formatTime(currentTime)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatTime(duration)}
              </Typography>
            </Box>
          </Box>

          {/* Credits */}
          <Box
            sx={{
              backgroundColor: theme.palette.mode === "dark" 
                ? "rgba(255,255,255,0.05)" 
                : "rgba(10, 114, 83, 0.04)",
              borderRadius: 1.5,
              p: 1.5,
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                display: "block",
                lineHeight: 1.6,
                fontStyle: "italic",
              }}
            >
              "Wavelengths – Alpha" by Chris Collins. Provided via Indie Music Box.
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
              <Typography
                variant="caption"
                fontWeight="700"
                sx={{ color: theme.palette.primary.main }}
              >
                Courtesy of Chris Collins:
              </Typography>
              <Link
                href="https://indiemusicbox.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  textDecoration: "underline",
                  "&:hover": {
                    color: theme.palette.primary.dark,
                  },
                }}
              >
                Visit indiemusicbox.com
                <OpenInNewIcon sx={{ fontSize: 13 }} />
              </Link>
            </Box>
          </Box>

          {/* About the Music - Expandable Section */}
          <Box>
            <Button
              fullWidth
              onClick={() => setExpandAboutMusic(!expandAboutMusic)}
              sx={{
                justifyContent: "space-between",
                color: theme.palette.text.primary,
                textTransform: "none",
                fontWeight: 600,
                mb: expandAboutMusic ? 1.5 : 0,
                px: 0,
                py: 1,
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <Typography variant="subtitle2">About the Music</Typography>
              {expandAboutMusic ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Button>

            {expandAboutMusic && (
              <Stack spacing={2}>
                {/* What are Alpha Waves */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    What are Alpha Waves?
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      mt: 0.75,
                      lineHeight: 1.6,
                    }}
                  >
                    Alpha waves are associated with a state of "relaxed alertness." They occur when you are calm and focused, but not yet in a deep sleep or high-stress state.
                  </Typography>
                </Box>

                {/* Best For */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Best For
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      mt: 0.75,
                      lineHeight: 1.6,
                    }}
                  >
                    Stress relief, light meditation, and preparation for deep mental work.
                  </Typography>
                </Box>

                {/* The Experience */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    The Experience
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      mt: 0.75,
                      lineHeight: 1.6,
                    }}
                  >
                    This 30-minute ambient soundscape by Chris Collins uses binaural beats to help transition the brain from a state of busy "Beta" activity into a centered "Alpha" flow.
                  </Typography>
                </Box>

                {/* Outcome */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Outcome
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      mt: 0.75,
                      lineHeight: 1.6,
                    }}
                  >
                    Using this track before a Check-in or Assessment helps lower cortisol levels, making it easier to provide honest, mindful responses.
                  </Typography>
                </Box>
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <Box sx={{ px: 3, py: 2 }}>
        <Button
          variant="text"
          href="https://indiemusicbox.com"
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
          sx={{
            color: theme.palette.primary.main,
            textTransform: "none",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "rgba(10, 114, 83, 0.06)",
            },
          }}
        >
          Explore Music Library
        </Button>
      </Box>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StressReliefDialog;
